import register from "preact-custom-element";
import {TemperatureAPI, User} from "./api/temperature";
import {Dashboard} from "./components/pages/dashboard";
import {Index} from "./components/pages";
import {Changelog} from "./components/pages/changelog";
import Router from "preact-router";

import "@fontsource/commissioner/100.css";
import "@fontsource/commissioner/200.css";
import "@fontsource/commissioner/300.css";
import "@fontsource/commissioner/400.css";
import "@fontsource/commissioner/500.css";
import "@fontsource/commissioner/600.css";
import "@fontsource/commissioner/700.css";
import "@fontsource/commissioner/800.css";
import "@fontsource/commissioner/900.css";
import "./style.css";
import {useEffect, useState} from "preact/hooks";
import {ComponentChildren, createContext} from "preact";
import {SensorDetail} from "./components/pages/sensorDetail";
import {IntlProvider} from "react-intl";
import {ComponentError, Error404} from "./components/error";
import {Login} from "./components/pages/login";
import {Admin} from "./components/pages/admin";
import {GlobalLoader} from "./components/generic/loader";
import {AlertContainer} from "./components/generic/alert";
import {createGlobalStyle, DefaultTheme, ThemeProvider} from "styled-components";
import LightTheme from "./theme/light";
import DarkTheme from "./theme/dark";
import {getLocalStorageItem, setLocalStorageItem} from "./lib/localStorage";
import {Sensor} from "./api/sensor";
import {SensorSet} from "./api/set";
import {GlobalFlashTarget, useFlash} from "./components/generic/flash";
import {APIError, APIResponseError} from "./api/base";
import {ComponentDebug} from "./components/pages/theme";

/**
 * TODO:
 * - Use popper for popup menus (with correct styling).
 */

export type AppProps = {
    clientid: string;
    refreshtokenendpoint: string;
    apiendpoint: string;
};

export type Route = {
    pathPrefix: string
    set?: SensorSet
    sensor?: Sensor;
}

export type GlobalData = {
    routes: Route[]
    user: User | null;
    version: string;
    title?: string | null;

    // TODO: This probably should not be here
    switchTheme: (newTheme: DefaultTheme) => void;
};

export type AsRoute = {
    path?: string;
    default?: true;
};

export const GlobalDataContext = createContext<GlobalData | null | undefined>(null);

const Themes = {
    "light": LightTheme,
    "dark": DarkTheme
};

const GlobalStyle = createGlobalStyle`
    body, html, .body {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        min-height: 100%;
        max-width: 100%;
    }
    
    body, .body {
        background: ${props => props.theme.colors.bg};
        color: ${props => props.theme.colors.fg};
        font-family: 'Commissioner', sans-serif;
        display: flex;
        flex-direction: column;
        line-height: 1.2rem;
    }
    
    a:link, a:visited {
        color: inherit;
        text-decoration: underline;
        
        &.link-invisible {
            display: block;
            text-decoration: none;
        }
    }
    
    a:active, a:hover {
        text-decoration: none;
        font-weight: bolder;
        
        &.link-invisible {
            text-decoration: inherit;
            font-weight: inherit;
        }
    }
    
    .text-left {
        text-align: left;
    }
    
    .text-center {
        text-align: center;
    }
    
    .text-right {
        text-align: right;
    }
    
    .text-danger {
        color: ${props => props.theme.accents.danger?.cardFg ?? props.theme.colors.cardFg} !important;
    }
    
    .text-muted {
        opacity: 0.6;
    }
    
    .td-shrink {
        width: 1px;
        white-space: nowrap;
    }
`;

export function App(props: AppProps) {
    const api = new TemperatureAPI(
        props.clientid,
        props.refreshtokenendpoint,
        props.apiendpoint
    );

    const [globalData, setGlobalData] = useState<GlobalData | null>(null);
    const [error, setError] = useState<unknown>();

    const [theme, setTheme] = useState<DefaultTheme>(
        (Themes as {[key: string]: DefaultTheme})[getLocalStorageItem<string>("theme", "light")]
    );

    const refreshGlobalData = async () => {
        try {
            const [sensor_routes, set_routes, currentUser, meta] = await Promise.all([
                api.sensor.get({filter: {}, attributes: ["id", "name", "url", "active"]})
                    .then(resp => resp.sensors.map<Route>(
                        sensor => {
                            return {pathPrefix: sensor.url as string, sensor: sensor}
                        }
                    )),
                api.sets.get()
                    .then(resp => resp.sensor_sets.map<Route>(
                        set => {
                            return {pathPrefix: set.url, set: set}
                        }
                    )),
                api.currentUser(),
                api.meta()
            ]);

            const globalData: GlobalData = {
                routes: [...sensor_routes, ...set_routes],
                user: currentUser,
                version: meta.version,
                title: meta.title,

                switchTheme: (newTheme: DefaultTheme) => {
                    setTheme(newTheme);
                    setLocalStorageItem("theme", newTheme.bodyClass);
                }
            };
            setGlobalData(globalData);
            console.log("Global data loaded as ", globalData);
        } catch (e) {
            setError(e);
        }
    }

    const {autoFlash} = useFlash();

    useEffect(() => {
        refreshGlobalData();

        window.addEventListener("unhandledrejection", (e) => {
            if (e.reason instanceof APIError) {
                let msg: ComponentChildren = e.reason.toString();

                if (e.reason instanceof APIResponseError) {
                    msg = <>
                        {e.reason.toString()}
                        {e.reason.response.headers.has("x-grid") && <><br/><small>Request identifier: <code>{e.reason.response.headers.get("x-grid")}</code></small></>}
                    </>;
                }

                autoFlash(msg, undefined, {accent: "danger", closable: true});
            } else {
                console.error(e);
            }
        })
    }, []);

    if (error) {
        throw error;
    }

    return <GlobalDataContext.Provider value={globalData}>
        <ThemeProvider theme={theme}>
            <GlobalStyle />
            <GlobalLoader />
            <GlobalFlashTarget />
            <AlertContainer />
            <ComponentError>
                <Router>
                    <Index path="/" api={api} />
                    <Changelog path="/changelog" api={api} />
                    {globalData?.routes.map(route => {
                        if (route.sensor) {
                            return <SensorDetail path={`${route.pathPrefix}/:tab*`} sensor={route.sensor} api={api} />;
                        } else if (route.set) {
                            return <Dashboard path={route.pathPrefix} set={route.set} api={api} />
                        }
                    })}
                    <Dashboard path="/dashboard/:setId" api={api} />
                    <SensorDetail path="/sensors/:sensorId/:subpath*" api={api} />
                    <Login path="/api/v3/oauth/authorize" />
                    <Admin path="/admin/:subpath*" api={api} />
                    <ComponentDebug path="/theme/:subpath*" />
                    <Error404 default />
                </Router>
            </ComponentError>
        </ThemeProvider>
    </GlobalDataContext.Provider>;
}

function HOC(props: AppProps) {
    return <IntlProvider locale={navigator.language ?? "en-US"}>
        <ComponentError>
            <App {...props} />
        </ComponentError>
    </IntlProvider>;
}

// This is the only entrypoint to the application, when everything is under React, this will be changed for
// render(<App />, document.body);
register(HOC, "temperature-app", ["clientId", "refreshTokenEndpoint", "apiEndpoint"]);
